import { Injectable } from '@angular/core';
import {
  Auth,
  AuthProvider,
  authState,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithCustomToken,
  IdTokenResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
  User,
  UserCredential
} from '@angular/fire/auth';
import { UserCredentials } from '@tix/shared/models';
import { from, Observable, of } from 'rxjs';
import { AuthStorage } from '..';

interface HasuraClaims {
  'x-hasura-user-id': string;
  'x-hasura-default-role': string;
  'x-hasura-allowed-roles': string[];
  'x-hasura-role': string;
  'x-hasura-company-id'?: string;
}

interface CustomClaims {
  'https://hasura.io/jwt/claims': HasuraClaims;
  [key: string]: any;
}

export interface CustomIdTokenResult extends IdTokenResult {
  claims: CustomClaims;
}

@Injectable()
export class TixAuthService {
  constructor(private auth: Auth) {
    this.auth.onAuthStateChanged(async user => {
      console.log('Auth state changed', user);
      if (!user) {
        AuthStorage.removeItem('token');
        AuthStorage.removeItem('role');
        AuthStorage.removeItem('selectedCompanyId');
        return;
      }

      const isInitialAuth = !AuthStorage.getItem('token');

      const token = await user.getIdToken();

      // On the frontoffice, the user's role and auth doesn't matter
      if (AuthStorage.getItem('isOnFrontoffice')) return;

      AuthStorage.setItem('token', token);
      if (isInitialAuth) AuthStorage.setItem('role', 'user');
    });
  }

  loadUser(): Observable<User | null> {
    return authState(this.auth);
  }

  emailPasswordLogin(credentials: UserCredentials): Observable<UserCredential> {
    return from(
      signInWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password
      )
    );
  }

  registerByEmail(createUser: UserCredentials): Observable<UserCredential> {
    return from(
      createUserWithEmailAndPassword(
        this.auth,
        createUser.email,
        createUser.password
      )
    );
  }

  logout(): Observable<any> {
    AuthStorage.clear();
    return from(signOut(this.auth));
  }

  signInMethod(method: 'google' | 'fb' | 'apple' | 'twitter') {
    let provider: AuthProvider = {} as AuthProvider;
    switch (method) {
      case 'google':
        provider = new GoogleAuthProvider();
        break;
      case 'fb':
        provider = new FacebookAuthProvider();
        break;
      case 'twitter':
        provider = new TwitterAuthProvider();
        break;
      case 'apple':
      default:
        break;
    }
    return from(signInWithPopup(this.auth, provider));
  }

  signInWithToken(token: string) {
    return from(signInWithCustomToken(this.auth, token));
  }

  getToken(): Observable<string | null> {
    if (!this.auth.currentUser) return of(null);

    return from(this.auth.currentUser!.getIdToken());
  }

  async refreshToken() {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return;

    return currentUser.getIdToken(true);
  }

  async getContactId(user?: User): Promise<string | undefined> {
    const tokenResult = (await (
      user ?? this.auth.currentUser
    )?.getIdTokenResult()) as CustomIdTokenResult | undefined;

    return tokenResult?.claims['https://hasura.io/jwt/claims']?.[
      'x-hasura-user-id'
    ];
  }

  async getHasuraClaims(): Promise<HasuraClaims | undefined> {
    const user = this.auth.currentUser;
    if (!user) return;

    const result = (await user.getIdTokenResult()) as CustomIdTokenResult;

    if (!result.claims) return;

    return result.claims['https://hasura.io/jwt/claims'];
  }
}
